import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ListChecks, MessageSquare, ShieldAlert, Trash2 } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { BATCH_STATUS_LABELS } from './BatchStatusDialog';
import { BatchSearch, ProductSearch, formatQty, invalidateStockControl, productTitle } from './stock-control-common';
import { BATCH_STATUSES, type BatchStatus, type BatchStatusResult, type ProductHit, type StockBatchHit } from './stock-control-types';

const BATCH_CONTROL_TAB = '/stock-control?tab=batch-control';
const MAX_BATCHES_PER_CHANGE = 200;

type TargetMode = 'batches' | 'lot';

interface PickedBatch {
  id: string;
  title: string;
  detail: string;
}

function pickedFromHit(batch: StockBatchHit): PickedBatch {
  return {
    id: batch.id,
    title: productTitle(batch.product),
    detail: `Batch ${batch.batchNo} · ${batch.store.name} · ${formatQty(batch.qtyOnHand)} ${batch.product.unit} on hand`,
  };
}

export function BatchStatusPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), BATCH_CONTROL_TAB);
  const [mode, setMode] = useState<TargetMode>('batches');
  const [status, setStatus] = useState<BatchStatus>('quarantined');
  const [picked, setPicked] = useState<PickedBatch[]>([]);
  const [product, setProduct] = useState<ProductHit | null>(null);
  const [batchNo, setBatchNo] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () =>
      post<BatchStatusResult>('/pharmacy/stock/batches/status', {
        ...(mode === 'batches' ? { stockBatchIds: picked.map((batch) => batch.id) } : { productId: product?.id, batchNo: batchNo.trim() }),
        status,
        reason: reason.trim(),
      }),
    onSuccess: (result) => {
      if (result.changed === 0) {
        toast.warning(tr('pharmacy', 'nothing_changed'), `${result.batches} batch${result.batches === 1 ? ' was' : 'es were'} already set to ${humanize(result.status, 'batch_status')}.`);
      } else {
        toast.success(
          `${result.changed} batch${result.changed === 1 ? '' : 'es'} set to ${humanize(result.status, 'batch_status')}`,
          result.status === 'active' ? tr('pharmacy', 'back_in_normal_stock') : tr('pharmacy', 'it_can_no_longer_be_sold_issued_or_transferred'),
        );
      }
      void invalidateStockControl(queryClient);
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const missing: string[] = [];
  if (mode === 'batches' && picked.length === 0) missing.push('at least one batch');
  if (mode === 'lot' && !product) missing.push('a product');
  if (mode === 'lot' && batchNo.trim() === '') missing.push('a batch number');
  if (reason.trim().length < 3) missing.push('a reason of 3 or more characters');

  const dirty = !change.isSuccess && (picked.length > 0 || product !== null || batchNo !== '' || reason !== '');

  return (
    <FormScreen
      title="Quarantine or recall stock"
      description={tr('pharmacy', 'blocked_stock_is_not_sold_issued_or_transferred')}
      eyebrow={tr('pharmacy', 'stock_control')}
      icon={ShieldAlert}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (missing.length > 0) return;
        setError(null);
        change.mutate();
      }}
    >
      <FormSection title={tr('pharmacy', 'action')} icon={ShieldAlert} columns={1}>
        <Field label={tr('pharmacy', 'action')} required>
          <ChoiceButtons
            layout="cards"
            columns={3}
            value={status}
            onChange={setStatus}
            options={BATCH_STATUSES.map((option) => ({ value: option, label: BATCH_STATUS_LABELS[option] }))}
          />
        </Field>
        <Field label={tr('pharmacy', 'which_stock')} required>
          <ChoiceButtons
            layout="cards"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'batches', label: tr('pharmacy', 'chosen_batches') },
              { value: 'lot', label: tr('pharmacy', 'a_lot_in_every_store_product_and_batch_number') },
            ]}
          />
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'batches')} icon={ListChecks} columns={1}>
        {mode === 'batches' && (
          <>
            <BatchSearch
              pickLabel="Add"
              isPicked={(batch) => picked.some((entry) => entry.id === batch.id)}
              onPick={(batch) =>
                setPicked((current) =>
                  current.length >= MAX_BATCHES_PER_CHANGE || current.some((entry) => entry.id === batch.id) ? current : [...current, pickedFromHit(batch)],
                )
              }
            />
            {picked.length === 0 ? (
              <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">{tr('pharmacy', 'no_batches_chosen_yet_search_above_and_add_the')}</p>
            ) : (
              <ul className="divide-y rounded-md border text-sm">
                {picked.map((batch) => (
                  <li key={batch.id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <span>
                      <span className="font-medium">{batch.title}</span>
                      <span className="block text-xs text-muted-foreground">{batch.detail}</span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={tr('pharmacy', 'remove_x_2', { title: batch.title })}
                      onClick={() => setPicked((current) => current.filter((entry) => entry.id !== batch.id))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {mode === 'lot' && (
          <div className="space-y-3">
            <Alert variant="warning">{tr('pharmacy', 'this_changes_every_batch_of_that_product_with')}</Alert>
            <ProductSearch selected={product} onSelect={setProduct} />
            <Field label={tr('pharmacy', 'batch_number')} required>
              <Input value={batchNo} maxLength={64} onChange={(event) => setBatchNo(event.target.value)} />
            </Field>
          </div>
        )}
      </FormSection>

      <FormSection title={tr('pharmacy', 'reason')} icon={MessageSquare} columns={1}>
        <Field label={tr('pharmacy', 'reason')} required hint={tr('pharmacy', 'kept_on_the_batch_history_at_least_3_characters')}>
          <Textarea rows={2} maxLength={300} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={status === 'active' ? tr('pharmacy', 'release') : status === 'recalled' ? tr('pharmacy', 'recall') : tr('pharmacy', 'quarantine')}
        loading={change.isPending}
        incomplete={missing.length > 0}
        destructive={status !== 'active'}
        cancelTo={returnTo}
        note={missing.length > 0 ? `${tr('pharmacy', 'still_needed')} ${missing.join(', ')}` : undefined}
      />
    </FormScreen>
  );
}
